import React, { useState, useEffect } from 'react';
import { Target, TrendingUp, ArrowRight, DollarSign, Zap, Brain, AlertTriangle } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

const PredictableGrowthAgent = () => {
  const [campaigns, setCampaigns] = useState([
    { name: "Instagram Ads", budget: 5000, roi: 120, status: "active" },
    { name: "Google Ads", budget: 3000, roi: 85, status: "active" },
    { name: "Facebook Ads", budget: 2000, roi: 200, status: "active" }
  ]);
  const [optimized, setOptimized] = useState(false);
  const [totalROI, setTotalROI] = useState(135);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!optimized) {
        // AI optimizes: moves budget from Google (low ROI) to Facebook (high ROI)
        setCampaigns([
          { name: "Instagram Ads", budget: 5000, roi: 120, status: "optimized" },
          { name: "Google Ads", budget: 1000, roi: 85, status: "reduced", change: "-$2000" },
          { name: "Facebook Ads", budget: 4000, roi: 200, status: "increased", change: "+$2000" }
        ]);
        setOptimized(true);
        setTotalROI(175);
      }
    }, 3000);

    return () => clearTimeout(timer);
  }, [optimized]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-green-50/30">
      <Header />

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-16">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h2 className="text-5xl font-bold text-gray-900 mb-6">
            Predictable Growth
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Watch AI optimize your marketing budget in real-time—cutting waste and maximizing ROI.
          </p>
        </div>

        {/* Animated Demo */}
        <div className="max-w-4xl mx-auto mb-12 md:mb-20">
          <div className="bg-white rounded-2xl md:rounded-3xl shadow-2xl border border-gray-200 overflow-hidden">
            {/* Overall ROI Header */}
            <div className={`p-4 md:p-8 transition-all duration-1000 ${optimized ? 'bg-gradient-to-r from-green-500 to-emerald-500' : 'bg-gradient-to-r from-indigo-500 to-purple-500'}`}>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-white/80 text-xs md:text-sm mb-1">Total Campaign ROI</div>
                  <div className="text-3xl md:text-5xl font-bold text-white">{totalROI}%</div>
                </div>
                {optimized && (
                  <div className="bg-white/20 backdrop-blur-sm rounded-xl md:rounded-2xl px-3 md:px-6 py-2 md:py-3 animate-fadeIn">
                    <div className="flex items-center space-x-1.5 md:space-x-2">
                      <Brain className="w-4 h-4 md:w-5 md:h-5 text-white" />
                      <span className="text-white font-semibold text-xs md:text-base">AI Optimized</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Campaigns */}
            <div className="p-4 md:p-8 bg-gradient-to-b from-gray-50 to-white">
              <div className="flex items-center justify-between mb-4 md:mb-6 gap-2">
                <h3 className="text-base md:text-lg font-semibold text-gray-900">Active Campaigns</h3>
                {!optimized && (
                  <div className="flex items-center space-x-1 md:space-x-2 text-xs md:text-sm text-orange-600">
                    <AlertTriangle className="w-3.5 h-3.5 md:w-4 md:h-4" />
                    <span className="hidden sm:inline">Analyzing performance...</span>
                    <span className="sm:hidden">Analyzing...</span>
                  </div>
                )}
              </div>

              <div className="space-y-3 md:space-y-4">
                {campaigns.map((campaign, index) => (
                  <div 
                    key={index} 
                    className={`border-2 rounded-xl md:rounded-2xl p-4 md:p-6 transition-all duration-700 ${
                      campaign.status === 'increased' ? 'border-green-300 bg-green-50' :
                      campaign.status === 'reduced' ? 'border-red-300 bg-red-50' :
                      campaign.status === 'optimized' ? 'border-blue-300 bg-blue-50' :
                      'border-gray-200 bg-white'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2 md:mb-3 gap-2">
                      <h4 className="font-semibold text-gray-900 text-sm md:text-base">{campaign.name}</h4>
                      <div className={`px-2 md:px-3 py-1 rounded-full text-[10px] md:text-xs font-medium whitespace-nowrap ${
                        campaign.roi >= 150 ? 'bg-green-200 text-green-800' :
                        campaign.roi >= 100 ? 'bg-blue-200 text-blue-800' :
                        'bg-orange-200 text-orange-800'
                      }`}>
                        {campaign.roi}% ROI
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center space-x-1.5 md:space-x-2 min-w-0">
                        <DollarSign className="w-3.5 h-3.5 md:w-4 md:h-4 text-gray-400 flex-shrink-0" />
                        <span className="text-gray-600 text-xs md:text-base truncate">${campaign.budget.toLocaleString()} budget</span>
                      </div>
                      
                      {campaign.change && (
                        <div className={`flex items-center space-x-1 px-2 md:px-3 py-1 rounded-full text-xs md:text-sm font-bold animate-fadeIn flex-shrink-0 ${
                          campaign.change.startsWith('+') ? 'bg-green-200 text-green-800' : 'bg-red-200 text-red-800'
                        }`}>
                          <TrendingUp className="w-3 h-3 md:w-4 md:h-4" />
                          <span>{campaign.change}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {optimized && (
                <div className="mt-8 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-2xl p-6 animate-fadeIn">
                  <div className="flex items-start space-x-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center flex-shrink-0">
                      <Zap className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-green-900 mb-1">Budget Optimized!</h4>
                      <p className="text-sm text-green-700">
                        Shifted $2,000 from low-performing Google Ads to high-performing Facebook Ads. 
                        <strong> ROI increased from 135% to 175%</strong> (+40%).
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-20">
          <div className="group bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-2xl transition-all duration-300 hover:scale-[1.02]">
            <div className="bg-gradient-to-br from-indigo-400 to-purple-500 rounded-xl p-4 w-fit mb-6 group-hover:scale-110 transition-transform duration-300">
              <Brain className="w-10 h-10 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">AI Optimization</h3>
            <p className="text-gray-600 leading-relaxed">
              Continuously improve campaigns for maximum ROI
            </p>
          </div>

          <div className="group bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-2xl transition-all duration-300 hover:scale-[1.02]">
            <div className="bg-gradient-to-br from-blue-400 to-cyan-500 rounded-xl p-4 w-fit mb-6 group-hover:scale-110 transition-transform duration-300">
              <Target className="w-10 h-10 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Smart Targeting</h3>
            <p className="text-gray-600 leading-relaxed">
              Find and reach your highest-value customers
            </p>
          </div>

          <div className="group bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-2xl transition-all duration-300 hover:scale-[1.02]">
            <div className="bg-gradient-to-br from-green-400 to-emerald-500 rounded-xl p-4 w-fit mb-6 group-hover:scale-110 transition-transform duration-300">
              <Zap className="w-10 h-10 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Real-Time Adjustments</h3>
            <p className="text-gray-600 leading-relaxed">
              Campaigns adapt automatically based on performance
            </p>
          </div>
        </div>

        {/* Benefits Section */}
        <div className="grid md:grid-cols-3 gap-8 mb-20">
          <div className="text-center">
            <div className="bg-gradient-to-br from-indigo-400 to-purple-500 rounded-2xl p-8 w-fit mx-auto mb-4 shadow-lg">
              <div className="text-4xl font-bold text-white">40%</div>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Higher ROI</h3>
          </div>
          <div className="text-center">
            <div className="bg-gradient-to-br from-blue-400 to-cyan-500 rounded-2xl p-8 w-fit mx-auto mb-4 shadow-lg">
              <DollarSign className="w-12 h-12 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Less waste</h3>
          </div>
          <div className="text-center">
            <div className="bg-gradient-to-br from-green-400 to-emerald-500 rounded-2xl p-8 w-fit mx-auto mb-4 shadow-lg">
              <TrendingUp className="w-12 h-12 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Consistent growth</h3>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center bg-gradient-to-r from-[#c4ffa0] to-green-400 rounded-2xl p-16 shadow-xl">
          <h3 className="text-3xl font-bold text-[#2d4a1e] mb-4">
            Ready for predictable growth?
          </h3>
          <p className="text-lg text-[#2d4a1e]/80 mb-8 max-w-2xl mx-auto">
            Start running data-driven campaigns that scale
          </p>
          <a
            href="https://calendly.com/kolanut"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center bg-[#2d4a1e] text-white px-8 py-4 rounded-xl font-semibold hover:bg-[#2d4a1e]/90 transition-all duration-300 hover:scale-105 shadow-lg"
          >
            Get Started
            <ArrowRight className="w-5 h-5 ml-2" />
          </a>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default PredictableGrowthAgent;
